/**
 * MediaFilter - Classe responsable du filtrage des médias selon les principes SOLID
 * Single Responsibility: Gère uniquement la logique de filtrage des médias
 */
class MediaFilter {
  /**
   * Extrait l'alt text d'un élément média
   * @param {HTMLElement} mediaElement - L'élément média
   * @returns {string} L'alt text extrait
   */
  static extractAltText(mediaElement) {
    if (mediaElement.tagName === 'IMG') {
      return String(mediaElement.getAttribute('alt') || '').trim();
    }
    const img = mediaElement.querySelector('img');
    return img ? String(img.getAttribute('alt') || '').trim() : '';
  }

  /**
   * Vérifie si un média est un thumbnail
   * @param {string} altText - L'alt text du média
   * @returns {boolean} True si c'est un thumbnail
   */
  static isThumbnail(altText) {
    if (!altText) return false;
    return altText.toLowerCase().includes('thumbnail');
  }

  /**
   * Détermine si un média doit être affiché pour un variant donné
   * @param {HTMLElement} mediaElement - L'élément média
   * @param {string} targetAltText - L'alt text cible du variant
   * @returns {boolean} True si le média doit être affiché
   */
  static shouldDisplayMedia(mediaElement, targetAltText) {
    if (!targetAltText || targetAltText === 'null') {
      return false;
    }

    const altText = this.extractAltText(mediaElement);
    
    // Ignorer si alt text est "null" ou vide
    if (!altText || altText === 'null') {
      return false;
    }

    // Exclure les thumbnails
    if (this.isThumbnail(altText)) {
      return false;
    }

    // Afficher uniquement si l'alt text correspond exactement au variant actuel
    return altText === targetAltText;
  }

  /**
   * Filtre les médias dans un conteneur selon un variant
   * @param {HTMLElement} container - Le conteneur des médias
   * @param {string} targetAltText - L'alt text cible du variant
   * @returns {boolean} True si au moins un média est visible
   */
  static filterMediaByVariant(container, targetAltText) {
    if (!container) return false;

    const allMedia = container.querySelectorAll('[data-media-id]');
    let hasVisibleMedia = false;

    allMedia.forEach(mediaElement => {
      const shouldShow = this.shouldDisplayMedia(mediaElement, targetAltText);
      
      if (shouldShow) {
        mediaElement.style.display = '';
        mediaElement.classList.remove('hidden');
        hasVisibleMedia = true;
      } else {
        mediaElement.style.display = 'none';
        mediaElement.classList.add('hidden');
      }
    });

    return hasVisibleMedia;
  }
}

/**
 * VariantDataExtractor - Classe responsable de l'extraction des données de variant
 * Single Responsibility: Gère uniquement l'extraction des données de variant
 */
class VariantDataExtractor {
  /**
   * Extrait l'alt text cible depuis un variant
   * @param {Object} currentVariant - Le variant actuel
   * @param {HTMLElement} contentContainer - Le conteneur de contenu pour fallback
   * @returns {string} L'alt text cible
   */
  static extractTargetAltText(currentVariant, contentContainer) {
    if (!currentVariant) return '';

    // Méthode 1: Utiliser featured_media.alt
    let targetAltText = String((currentVariant.featured_media && currentVariant.featured_media.alt) || '').trim();
    
    // Méthode 2: Fallback sur currentVariant.color
    if (!targetAltText && currentVariant.color) {
      targetAltText = String(currentVariant.color).trim();
    }
    
    // Méthode 3: Déduire depuis les options qui correspondent aux alt présents dans le DOM
    if (!targetAltText && contentContainer) {
      const altSet = new Set(
        Array.from(contentContainer.querySelectorAll('img[alt]'))
          .map(img => (img.getAttribute('alt') || '').trim())
          .filter(alt => alt && !alt.toLowerCase().includes('thumbnail'))
          .map(alt => alt.toLowerCase())
      );
      
      const candidates = [];
      if (Array.isArray(currentVariant.options)) candidates.push(...currentVariant.options);
      if (currentVariant.option1) candidates.push(currentVariant.option1);
      if (currentVariant.option2) candidates.push(currentVariant.option2);
      if (currentVariant.option3) candidates.push(currentVariant.option3);
      
      const match = candidates.find(v => v && altSet.has(String(v).trim().toLowerCase()));
      if (match) {
        targetAltText = String(match).trim();
      }
    }

    return targetAltText;
  }
}

if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.isDragging = false;
      this.startX = 0;
      this.scrollLeft = 0;
      this.mediaFilter = MediaFilter;
      this.variantDataExtractor = VariantDataExtractor;
    }

    connectedCallback() {
      super.connectedCallback();
      // Desktop only: initialiser drag-to-scroll et filtrage par variant
      if (this.isDesktop()) {
        this.initDragToScroll();
        this.initVariantChangeListener();
        this.initializeModalContent();
      }
    }

    /**
     * Vérifie si on est en mode desktop
     * @returns {boolean} True si desktop
     */
    isDesktop() {
      return window.matchMedia('(min-width: 750px)').matches;
    }

    initializeModalContent() {
      // Desktop only
      if (!this.isDesktop()) return;

      // Filtrer immédiatement avec les données disponibles
      const content = this.querySelector('.product-media-modal__content');
      if (content) {
        this.removeNullTextNodes(content);
        // Filtrer immédiatement même si le variant controller n'est pas prêt
        this.filterMediaByDefaultVariant(content);
      }

      // Attendre que le DOM soit prêt pour une mise à jour plus précise
      const updateContent = () => {
        this.updateModalContentFromCurrentVariant();
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', updateContent);
      } else {
        // Utiliser requestAnimationFrame pour s'assurer que le variant controller est initialisé
        requestAnimationFrame(() => {
          requestAnimationFrame(updateContent);
        });
      }
    }

    updateModalContentFromCurrentVariant() {
      // Desktop only
      if (!this.isDesktop()) return;

      const ctrl = this.getVariantController();
      const content = this.querySelector('.product-media-modal__content');
      
      if (!content) return;

      // Nettoyer les nœuds de texte "null" dès l'initialisation
      this.removeNullTextNodes(content);

      // Filtrer les médias même si le variant controller n'est pas encore prêt
      // En utilisant le variant par défaut depuis l'URL ou le premier disponible
      let currentVariant = null;
      
      if (ctrl && ctrl.currentVariant) {
        currentVariant = ctrl.currentVariant;
      } else {
        // Essayer de récupérer le variant depuis l'URL
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
        
        if (variantId && ctrl && ctrl.getVariantData) {
          const variants = ctrl.getVariantData();
          currentVariant = variants.find(v => v.id == variantId);
        }
      }

      // Si on a un variant, filtrer les médias
      if (currentVariant) {
        this.updateModalContent(currentVariant);
      } else {
        // Sinon, masquer tous les médias sauf ceux du variant par défaut
        // On va utiliser le premier média visible comme référence
        this.filterMediaByDefaultVariant(content);
      }
    }

    /**
     * Filtre les médias selon le variant par défaut (premier disponible)
     * @param {HTMLElement} content - Le conteneur de contenu
     */
    filterMediaByDefaultVariant(content) {
      // Essayer d'obtenir le variant par défaut depuis le variant controller
      const ctrl = this.getVariantController();
      let defaultAltText = '';

      // Méthode 1: Utiliser le premier variant disponible depuis les données JSON
      if (ctrl && ctrl.getVariantData) {
        try {
          const variants = ctrl.getVariantData();
          const firstAvailableVariant = variants.find(v => 
            v.available && v.featured_media && v.featured_media.id
          );
          
          if (firstAvailableVariant) {
            // Essayer d'extraire l'alt text depuis le variant
            if (firstAvailableVariant.color) {
              defaultAltText = String(firstAvailableVariant.color).trim();
            } else if (firstAvailableVariant.featured_media && firstAvailableVariant.featured_media.alt) {
              defaultAltText = String(firstAvailableVariant.featured_media.alt).trim();
            } else if (firstAvailableVariant.options && firstAvailableVariant.options.length > 0) {
              // Utiliser la première option (généralement la couleur)
              defaultAltText = String(firstAvailableVariant.options[0]).trim();
            }
          }
        } catch (e) {
          console.warn('Error getting variant data:', e);
        }
      }

      // Méthode 2: Si pas de variant trouvé, utiliser le premier média non-thumbnail
      if (!defaultAltText) {
        const allMedia = content.querySelectorAll('[data-media-id]');
        for (const mediaElement of allMedia) {
          const altText = this.mediaFilter.extractAltText(mediaElement);
          if (altText && altText !== 'null' && !this.mediaFilter.isThumbnail(altText)) {
            defaultAltText = altText;
            break;
          }
        }
      }

      // Filtrer les médias avec l'alt text trouvé
      if (defaultAltText) {
        this.mediaFilter.filterMediaByVariant(content, defaultAltText);
      } else {
        // Sinon, masquer tous les médias
        const allMedia = content.querySelectorAll('[data-media-id]');
        allMedia.forEach(el => {
          el.style.display = 'none';
          el.classList.add('hidden');
        });
      }
    }

    initVariantChangeListener() {
      // Écouter les changements de variant
      document.addEventListener('variant:change', (e) => {
        if (e.detail && e.detail.variant && e.detail.section) {
          this.reloadModalContent(e.detail.variant, e.detail.section);
        }
      });
    }

    // Récupère l'élément contrôleur de variantes (radios ou selects)
    getVariantController(sectionId) {
      const sel = sectionId
        ? `variant-radios[data-section="${sectionId}"], variant-selects[data-section="${sectionId}"]`
        : 'variant-radios, variant-selects';
      return document.querySelector(sel);
    }

    reloadModalContent(currentVariant, sectionId) {
      if (!currentVariant || !sectionId) {
        return Promise.reject('Missing variant or sectionId');
      }
      
      const variantSelects = this.getVariantController(sectionId);
      if (!variantSelects || !variantSelects.dataset || !variantSelects.dataset.url) {
        return Promise.reject('VariantSelects not found');
      }

      const content = this.querySelector('.product-media-modal__content');
      if (!content) {
        return Promise.reject('Modal content not found');
      }

      // Recharger le contenu du modal depuis le serveur avec le nouveau variant
      const url = `${variantSelects.dataset.url}?variant=${currentVariant.id}&section_id=${sectionId}`;
      
      return fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const newModal = html.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
          
          if (newModal) {
            // Remplacer le contenu
            content.innerHTML = newModal.innerHTML;
            // Nettoyer les nœuds de texte "null" qui peuvent apparaître
            this.removeNullTextNodes(content);
            // Réinitialiser le drag-to-scroll
            this.initDragToScroll();
            // Filtrer les médias selon le variant actuel
            this.updateModalContent(currentVariant);
            return Promise.resolve();
          } else {
            return Promise.reject('New modal content not found');
          }
        })
        .catch((error) => {
          console.error('Error reloading modal content:', error);
          // Fallback : essayer de filtrer avec ce qui existe déjà
          this.updateModalContent(currentVariant);
          return Promise.reject(error);
        });
    }

    removeNullTextNodes(container) {
      if (!container) return;
      
      // Parcourir tous les nœuds de texte
      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      
      const textNodesToRemove = [];
      let node;
      
      while (node = walker.nextNode()) {
        // Vérifier si le nœud de texte contient uniquement "null", des espaces ou est vide
        const text = node.textContent.trim();
        // Supprimer les nœuds qui contiennent uniquement "null" (insensible à la casse) ou sont vides
        if (text === 'null' || text === '' || /^\s*null\s*$/i.test(node.textContent)) {
          textNodesToRemove.push(node);
        }
      }
      
      // Supprimer les nœuds de texte "null"
      textNodesToRemove.forEach(textNode => {
        textNode.remove();
      });
    }

    /**
     * Met à jour le contenu du modal selon le variant actuel
     * Desktop only: filtre les médias pour n'afficher que ceux du variant
     * @param {Object} currentVariant - Le variant actuel
     */
    updateModalContent(currentVariant) {
      // Desktop only
      if (!this.isDesktop()) return;

      const content = this.querySelector('.product-media-modal__content');
      if (!content) return;

      // S'assurer que le contenu est visible
      content.style.display = '';

      // Nettoyer les nœuds de texte "null" qui pourraient s'afficher
      this.removeNullTextNodes(content);

      // Si pas de variant, masquer les médias mais garder le conteneur
      if (!currentVariant) {
        content.querySelectorAll('[data-media-id]').forEach(el => {
          el.style.display = 'none';
          el.classList.add('hidden');
        });
        return;
      }

      // Utiliser VariantDataExtractor pour extraire l'alt text cible
      const targetAltText = this.variantDataExtractor.extractTargetAltText(currentVariant, content);
      
      if (!targetAltText || targetAltText === 'null') {
        content.querySelectorAll('[data-media-id]').forEach(el => {
          el.style.display = 'none';
          el.classList.add('hidden');
        });
        return;
      }

      // Utiliser MediaFilter pour filtrer les médias
      const hasVisibleMedia = this.mediaFilter.filterMediaByVariant(content, targetAltText);

      // Si aucun média visible, afficher le premier média disponible (fallback)
      if (!hasVisibleMedia) {
        const firstMedia = content.querySelector('[data-media-id]');
        if (firstMedia) {
          firstMedia.style.display = '';
          firstMedia.classList.remove('hidden');
        }
      }
    }

    initDragToScroll() {
      const content = this.querySelector('.product-media-modal__content');
      if (!content) return;

      // Desktop only
      if (this.isDesktop()) {
        content.style.userSelect = 'none';

        const handleMouseDown = (e) => {
          // Ne pas démarrer le drag si on clique sur un élément interactif
          if (e.target.closest('button, a, video, iframe, model-viewer')) {
            return;
          }

          this.isDragging = true;
          content.classList.add('is-dragging');
          const rect = content.getBoundingClientRect();
          this.startX = e.pageX - rect.left;
          // Sauvegarder la position actuelle du scroll dans dataset
          content.dataset.mouseDownAt = e.pageX.toString();
          content.dataset.scrollLeft = content.scrollLeft.toString();
          e.preventDefault();
          e.stopPropagation();
        };

        const handleMouseLeave = () => {
          this.isDragging = false;
          content.classList.remove('is-dragging');
        };

        const handleMouseUp = (e) => {
          if (this.isDragging) {
            this.isDragging = false;
            content.classList.remove('is-dragging');
            // Mettre à jour la position sauvegardée dans dataset
            content.dataset.scrollLeft = content.scrollLeft.toString();
          }
        };

        const handleMouseMove = (e) => {
          if (!this.isDragging) return;
          e.preventDefault();
          e.stopPropagation();
          
          // Récupérer la position sauvegardée depuis dataset
          const scrollLeftAtMouseDown = parseFloat(content.dataset.scrollLeft) || 0;
          const mouseDownAt = parseFloat(content.dataset.mouseDownAt) || e.pageX;
          
          const walk = (e.pageX - mouseDownAt) * 2; // Multiplier pour un scroll plus rapide
          // Utiliser la position sauvegardée comme référence
          content.scrollLeft = scrollLeftAtMouseDown - walk;
        };

        // Ajouter les listeners sur le document pour capturer même si la souris sort du conteneur
        const handleDocumentMouseMove = (e) => {
          if (!this.isDragging) return;
          handleMouseMove(e);
        };

        const handleDocumentMouseUp = (e) => {
          handleMouseUp(e);
        };

        content.addEventListener('mousedown', handleMouseDown);
        content.addEventListener('mouseleave', handleMouseLeave);
        document.addEventListener('mousemove', handleDocumentMouseMove);
        document.addEventListener('mouseup', handleDocumentMouseUp);

        // Mouse wheel to horizontal scroll with native smooth scrolling
        let scrollTarget = 0;
        const handleWheel = (e) => {
          e.preventDefault();
          // Use deltaY for vertical scroll wheel, convert to horizontal
          const scrollAmount = (e.deltaY || e.deltaX) * 3; // Multiplier for scroll speed
          
          // Update target and clamp to scroll bounds
          scrollTarget = content.scrollLeft + scrollAmount;
          scrollTarget = Math.max(0, Math.min(scrollTarget, content.scrollWidth - content.clientWidth));
          
          // Use native smooth scroll (no fps limit, hardware accelerated)
          content.scrollTo({
            left: scrollTarget,
            behavior: 'smooth'
          });
        };
        
        content.addEventListener('wheel', handleWheel, { passive: false });

        // Cleanup on hide
        this._dragHandlers = {
          mousedown: handleMouseDown,
          mouseleave: handleMouseLeave,
          mouseup: handleDocumentMouseUp,
          mousemove: handleDocumentMouseMove,
          wheel: handleWheel
        };
      }
    }

    hide() {
      super.hide();
      this.isDragging = false;
      
      // Cleanup event listeners
      const content = this.querySelector('.product-media-modal__content');
      if (content) {
        content.classList.remove('is-dragging');
        if (this._dragHandlers) {
          content.removeEventListener('mousedown', this._dragHandlers.mousedown);
          content.removeEventListener('mouseleave', this._dragHandlers.mouseleave);
          document.removeEventListener('mouseup', this._dragHandlers.mouseup);
          document.removeEventListener('mousemove', this._dragHandlers.mousemove);
          if (this._dragHandlers.wheel) {
            content.removeEventListener('wheel', this._dragHandlers.wheel);
          }
          this._dragHandlers = null;
        }
      }
    }

    show(opener) {
      super.show(opener);
      
      // Mobile: comportement simple comme l'ancien code
      if (!this.isDesktop()) {
        this.showActiveMedia();
        return;
      }
      
      // Desktop: mettre à jour le contenu du modal avec le variant actuel
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const variantSelects = this.getVariantController();
          if (variantSelects && variantSelects.currentVariant && variantSelects.dataset.section) {
            // Recharger le contenu du modal pour avoir les médias du variant actuel
            this.reloadModalContent(variantSelects.currentVariant, variantSelects.dataset.section).then(() => {
              this.showActiveMedia();
            }).catch(() => {
              // Si le rechargement échoue, essayer de filtrer avec ce qui existe
              this.updateModalContent(variantSelects.currentVariant);
              this.showActiveMedia();
            });
          } else {
            // Si pas de variant, essayer de trouver depuis l'opener
            const mediaId = opener.getAttribute('data-media-id');
            if (mediaId) {
              const mediaIdParts = mediaId.split('-');
              if (mediaIdParts.length > 1) {
                const actualMediaId = mediaIdParts.slice(1).join('-');
                const allVariants = variantSelects ? variantSelects.getVariantData() : [];
                const matchingVariant = allVariants.find(v => v.featured_media && v.featured_media.id == actualMediaId);
                if (matchingVariant) {
                  this.updateModalContent(matchingVariant);
                }
              }
            }
            this.showActiveMedia();
          }
        });
      });
    }

    showActiveMedia() {
      const mediaId = this.openedBy.getAttribute("data-media-id");
      if (!mediaId) return;

      const isDesktop = this.isDesktop();

      // Retirer la classe active de tous les médias
      if (isDesktop) {
        // Desktop: seulement parmi les médias visibles (filtrés)
        this.querySelectorAll(`[data-media-id]:not(.hidden)`).forEach((element) => {
          element.classList.remove('active');
        });
      } else {
        // Mobile: tous les médias sauf celui qui va être actif
        this.querySelectorAll(`[data-media-id]:not([data-media-id="${mediaId}"])`).forEach((element) => {
          element.classList.remove('active');
        });
      }

      // Trouver le média actif
      const activeMedia = isDesktop 
        ? this.querySelector(`[data-media-id="${mediaId}"]:not(.hidden)`)
        : this.querySelector(`[data-media-id="${mediaId}"]`);

      if (!activeMedia) {
        if (isDesktop) {
          // Desktop: chercher le premier média visible
          const firstVisible = this.querySelector(`[data-media-id]:not(.hidden)`);
          if (firstVisible) {
            firstVisible.classList.add('active');
            this.scrollToMedia(firstVisible);
          }
        }
        return;
      }

      const activeMediaTemplate = activeMedia.querySelector('template');
      const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
      activeMedia.classList.add('active');

      if (isDesktop) {
        // Desktop: scroll horizontal smooth
        this.scrollToMedia(activeMedia);
      } else {
        // Mobile: comportement original avec scrollIntoView
        activeMedia.scrollIntoView();
        const container = this.querySelector('[role="document"]');
        if (container) {
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        }
      }

      if (activeMedia.nodeName == 'DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube'))
        activeMedia.loadContent();
    }

    scrollToMedia(mediaElement) {
      // Ne s'applique que sur desktop
      if (!this.isDesktop()) return;
      
      const container = this.querySelector('.product-media-modal__content');
      if (!container) return;

      // Déplacer immédiatement le scroll horizontal pour afficher le média au plus vite
      const offsetLeft = mediaElement.offsetLeft;
      container.scrollTo({ left: offsetLeft, behavior: 'smooth' });
    }
  });
}
